import { useCallback, useEffect, useRef, useState } from 'react';
import {
    DocumentNode,
    OperationVariables,
    QueryHookOptions,
    QueryResult,
    useQuery,
} from '@apollo/client';
import { takeQueryTurn } from 'src/projects/utils/priceQueryQueue.ts';
import {
    readSelectionData,
    selectionDataKey,
    touchSelectionData,
    writeSelectionData,
} from 'src/projects/utils/selectionDataCache.ts';

/**
 * Runs a selection's price query, but only when the row's turn comes up — and only when the
 * result is not already in memory.
 *
 * The offline GraphQL resolvers run on the main thread and never yield, so a page of rows that
 * queries all at once blocks the browser for the sum of every query: nothing about the list —
 * names, order, count — reaches the screen until the last price is calculated. Taking turns
 * moves the first query out of the initial render's task and keeps every later one in a task of
 * its own, so the list frame paints immediately and the prices fill in row by row.
 *
 * Every result is then kept (see selectionDataCache), which is what makes paging back and forth
 * free: a row whose product was priced before renders its price straight away, without a query
 * and without waiting in line.
 *
 * Until its turn comes the query is skipped, which the callers already treat as "loading" and
 * render as a SkeletonPrice.
 */
export const usePriceQuery = <TData, TVariables extends OperationVariables>(
    query: DocumentNode,
    options: QueryHookOptions<TData, TVariables>,
): QueryResult<TData, TVariables> => {
    const key = selectionDataKey(query, options.variables);
    const cached = readSelectionData<TData>(key);
    const isCached = cached !== undefined;

    const [allowed, setAllowed] = useState<boolean>(false);
    const release = useRef<(() => void) | null>(null);

    const giveUpTurn = useCallback(() => {
        release.current?.();
        release.current = null;
    }, []);

    useEffect(() => {
        if (isCached) {
            touchSelectionData(key);
            return undefined;
        }
        release.current = takeQueryTurn(() => setAllowed(true));
        // Covers a row that goes away (pagination, moved to another page) before it was served.
        return giveUpTurn;
    }, [isCached, key, giveUpTurn]);

    const result = useQuery<TData, TVariables>(query, {
        ...options,
        skip: isCached || !allowed,
    });
    const { data, error } = result;

    useEffect(() => {
        if (data !== undefined) {
            writeSelectionData(key, data);
        }
        if (data !== undefined || error !== undefined) {
            giveUpTurn();
        }
    }, [data, error, key, giveUpTurn]);

    return isCached
        ? { ...result, data: cached }
        : result;
};
